import { isTransientError, useAuth0 } from '@brighte/passwordless-authenticator';
import { ApplicationStep, EnterpriseStructure, RepaymentType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import {
  ApplicationDocumentType,
  ApplicationEntityType,
  ErrorResponse,
  FormValues,
  MutationResponse,
  UpdateApplicationInput,
  useCreateApplicationDocumentMutation,
  useCreateApplicationFromQuoteMutation,
  useUpdateApplicationDocumentStatusUpdatedMutation,
  useUpdateApplicationMutation,
} from '../../graphql';
import { useAddJointApplicant, useResizeDocumentForUpload, useUploadFilesToS3 } from '../../hooks';
import { useApplicationData, useSetApplicationData } from '../../provider';
import { useSavePaymentDetails } from '../../steps/RepaymentAccount/hooks';
import { APPLICATION_STEPS, getApplicantTypeAndTrackingIdFromUrl, isJointApplicant } from '../../utils';
import { transformCreateApplicationInput, transformUpdateApplicationInput } from './helper';

type SaveStepResponse = (value: { isStepSaved: boolean; message?: string }) => void;

interface TrustEnterpriseData {
  structure?: EnterpriseStructure;
  trustDeedFiles?: File[];
  trustDeedDocumentIds?: string[];
  trustDeedDocumentNames?: string[];
}

const checkIfSameAccountNumber = (
  formPayment?: FormValues['PaymentDetails']['payment'],
  applicationPayment?: FormValues['PaymentDetails']['payment'],
): boolean => {
  if (!formPayment || !applicationPayment) return false;

  switch (formPayment.type) {
    case RepaymentType.DirectDebit:
      return (
        applicationPayment.type === RepaymentType.DirectDebit &&
        applicationPayment?.directDebit?.accountNumber === formPayment?.directDebit?.accountNumber
      );
    case RepaymentType.CreditCard:
      return (
        applicationPayment.type === RepaymentType.CreditCard &&
        applicationPayment?.creditCard?.token === formPayment?.creditCard?.token
      );
    default:
      return false;
  }
};

const handleUpdateFailure = (errorResponse: unknown, resolve: SaveStepResponse) => {
  const errorMessage = (errorResponse as ErrorResponse)?.response?.errors?.[0]?.message;
  datadogRum.addAction('Failed to update application', {
    name: 'Failed Update Application',
    values: {
      errorResponse,
      errorMessage,
    },
  });
  resolve({ isStepSaved: false, message: errorMessage });
};

export const useSaveStepDetails = () => {
  const setApplicationData = useSetApplicationData();
  const applicationData = useApplicationData() as Partial<FormValues>;
  const { mutate: createApplicationMutation } = useCreateApplicationFromQuoteMutation({
    // No useErrorBoundary override here: this falls back to the shared client's auth-aware
    // default (see apps/passwordless-authenticator/src/graphql/clients/reactQuery.ts), so an
    // expired session still throws to the error boundary's re-login dialog, while every other
    // failure — including a retry-exhausted transient one — is handled below by `onError`,
    // keeping the customer on the form with their progress intact instead of a full-page crash.
    //
    // A brief backend blip (e.g. a 502 during a deploy) shouldn't strand the customer on an
    // error at all if it would have succeeded a moment later — retry transient failures a
    // couple of times before giving up.
    retry: (failureCount, error) => isTransientError(error) && failureCount < 2,
    retryDelay: retryCount => 1000 * 2 ** retryCount,
  });
  const { mutate: updateApplication } = useUpdateApplicationMutation();
  const { mutate: addJointApplicant } = useAddJointApplicant();
  const { onSavePaymentDetails } = useSavePaymentDetails();
  const { user } = useAuth0();
  const quote = useApplicationData('quote');

  // Trust deed file upload mutations
  const { mutateAsync: createUploadDocument } = useCreateApplicationDocumentMutation();
  const { mutateAsync: uploadFileToS3 } = useUploadFilesToS3();
  const { mutateAsync: updateDocumentStatus } = useUpdateApplicationDocumentStatusUpdatedMutation();
  const resizeDocumentForUpload = useResizeDocumentForUpload();

  const uploadSingleFile = async (
    file: File,
    applicationId: string,
    applicantType: string,
  ): Promise<{ id: string; filename: string }> => {
    // Prepare photos before upload, including the JPEG filename after HEIC/HEIF conversion.
    const uploadFile = await resizeDocumentForUpload(file);
    const data = await createUploadDocument({
      createApplicationDocumentInput: {
        applicationId,
        filename: uploadFile.name,
        fileSize: uploadFile.size,
        type: ApplicationDocumentType.Others,
        entityType: ApplicationEntityType[applicantType],
      },
    });

    const createApplicationDocument = data?.createApplicationDocument;
    if (!createApplicationDocument) {
      throw new Error(`Failed to create upload document for file ${file.name}`);
    }

    const { document, url, fieldsJson } = createApplicationDocument;

    if (!url || !fieldsJson) {
      throw new Error(`Failed to get presigned URL for file ${file.name}`);
    }

    const uploadMetaData = JSON.parse(fieldsJson);
    const formData = new FormData();
    for (const key of Object.keys(uploadMetaData)) {
      formData.append(key, uploadMetaData[key]);
    }
    formData.append('file', uploadFile);

    await uploadFileToS3({ bucketUrl: url, file: formData });
    await updateDocumentStatus({
      updateApplicationDocumentStatusUpdatedInput: {
        documentId: document.id,
        applicationId,
      },
    });

    return { id: document.id, filename: document.filename };
  };

  const saveStep = (newFormValues: Partial<FormValues>) =>
    new Promise((resolve: SaveStepResponse) => {
      const currentPage = Object.keys(newFormValues)[0] as ApplicationStep;
      const formValues = { ...applicationData, ...newFormValues };

      const stepEventNames: Partial<Record<ApplicationStep, string>> = {
        [APPLICATION_STEPS.INCOME_VERIFICATION]: 'Finance Application Employment Details Submitted',
        [APPLICATION_STEPS.FINANCE_PURCHASE]: 'Finance Application Repayment Terms Submitted',
        [APPLICATION_STEPS.ELIGIBILITY]: 'Finance Application Eligibility Submitted',
        [APPLICATION_STEPS.IDENTIFICATION]: 'Finance Application Identification Submitted',
        [APPLICATION_STEPS.HOUSEHOLD]: 'Finance Application Household Details Submitted',
        [APPLICATION_STEPS.REPAYMENT_ACCOUNT]: 'Finance Application Payment Details Submitted',
        [APPLICATION_STEPS.SUMMARY]: 'Finance Application Sent',
        [APPLICATION_STEPS.BUSINESS_DETAILS]: 'Finance Application Business Details Submitted',
        [APPLICATION_STEPS.OWNERSHIP_DETAILS]: 'Finance Application Ownership Details Submitted',
        [APPLICATION_STEPS.BENEFICIAL_OWNER]: 'Finance Application Beneficial Owner Submitted',
      };

      const trackStepSubmitted = () => {
        const eventName = stepEventNames[currentPage];
        if (!eventName) return;
        if (!quote?.id || !user?.uid) return;
        datadogRum.addAction(eventName, {
          quote_id: quote?.id,
          loan_type: quote?.financialProduct?.id,
          user_id: user?.uid,
          role_type: user?.role,
        });
      };

      const submitStep = (
        transformedApplicationInput?: UpdateApplicationInput | null,
        additionalFormValues?: Partial<FormValues>,
      ) => {
        const applicationInput =
          transformedApplicationInput !== undefined
            ? transformedApplicationInput
            : transformUpdateApplicationInput({ currentPage, formValues });

        if (applicationInput === null) {
          resolve({ isStepSaved: false });
          return;
        }

        updateApplication(
          { input: applicationInput },
          {
            onSuccess: (data: Record<string, unknown>) => {
              const storePayload = {
                ...newFormValues,
                ...additionalFormValues,
                applicationId: (data?.updateApplication as MutationResponse)?.applicationId,
              };
              setApplicationData(storePayload);
              trackStepSubmitted();
              resolve({ isStepSaved: true });
            },
            onError: (errorResponse: unknown) => handleUpdateFailure(errorResponse, resolve),
          },
        );
      };

      const handleTrustDeedUpload = async () => {
        const previousStructure = applicationData.BusinessDetails?.enterprise?.structure;
        const enterprise = formValues.BusinessDetails?.enterprise;
        const isTrustEnterprise = enterprise?.structure === EnterpriseStructure.Trust;
        const additionalData: Partial<FormValues> = {};

        // Clear BeneficialOwner and GuarantorDetails if the structure has changed
        if (previousStructure !== enterprise?.structure) {
          additionalData.BeneficialOwner = {};
          additionalData.GuarantorDetails = {};
        }

        // Only run trust deed upload logic when the current enterprise structure is Trust.
        // For non-Trust structures, bypass entirely so stale Trust files in the
        // application store cannot trigger trust deed uploads.
        if (!isTrustEnterprise) {
          submitStep(undefined, additionalData);
          return;
        }

        const trustEnterprise = enterprise;

        // trustDeedFiles may not be in the submitted form values because the field is
        // set via setValue and the form uses shouldUnregister: true (unregistered fields
        // are excluded from handleSubmit output). Fall back to the application data store
        // which is updated when the user drops files, but only if the stored enterprise
        // is also a Trust to avoid using stale Trust data.
        const storedEnterprise = applicationData.BusinessDetails?.enterprise as TrustEnterpriseData | undefined;
        const storeTrustEnterprise =
          storedEnterprise?.structure === EnterpriseStructure.Trust ? storedEnterprise : undefined;
        const trustDeedFiles = (trustEnterprise?.trustDeedFiles ?? storeTrustEnterprise?.trustDeedFiles) as
          | File[]
          | undefined;
        const hasTrustDeedFiles = !!(trustDeedFiles && trustDeedFiles.length > 0);

        // Check for existing uploaded documents (previously saved trust deeds)
        const existingDocumentIds = storeTrustEnterprise?.trustDeedDocumentIds;
        const hasExistingDocuments = !!(existingDocumentIds && existingDocumentIds.length > 0);

        if (!hasTrustDeedFiles) {
          // No new files to upload. Preserve existing document IDs in the form values
          // so transformUpdateApplicationInput includes them in the API payload and
          // setApplicationData retains them in the store.
          if (hasExistingDocuments) {
            const existingDocNames = storeTrustEnterprise?.trustDeedDocumentNames;
            const businessDetailsWithExistingDocs = {
              ...formValues.BusinessDetails,
              enterprise: {
                ...enterprise,
                trustDeedDocumentIds: existingDocumentIds,
                ...(existingDocNames && { trustDeedDocumentNames: existingDocNames }),
              },
            } as FormValues['BusinessDetails'];
            const preservedFormValues = {
              ...formValues,
              BusinessDetails: businessDetailsWithExistingDocs,
            } as FormValues;

            const applicationInput = transformUpdateApplicationInput({
              currentPage,
              formValues: preservedFormValues,
            });
            submitStep(applicationInput, { ...additionalData, BusinessDetails: businessDetailsWithExistingDocs });
          } else {
            // Trust structure requires at least a trust deed file or previously uploaded document.
            resolve({ isStepSaved: false, message: 'Trust deed is a required field' });
          }
          return;
        }

        const { applicantType } = getApplicantTypeAndTrackingIdFromUrl();
        const applicationId = applicationData.applicationId;

        if (!applicationId) {
          resolve({ isStepSaved: false, message: 'Application must be saved before uploading trust deed documents' });
          return;
        }

        try {
          const uploadedDocuments = await Promise.all(
            trustDeedFiles.map(file => uploadSingleFile(file, applicationId, applicantType)),
          );
          const documentIds = uploadedDocuments.map(doc => doc.id);
          const documentNames = uploadedDocuments.map(doc => doc.filename);
          const businessDetailsWithTrustDeed = {
            ...formValues.BusinessDetails,
            enterprise: {
              ...enterprise,
              trustDeedFiles: undefined,
              trustDeedDocumentIds: documentIds,
              trustDeedDocumentNames: documentNames,
            },
          } as FormValues['BusinessDetails'];
          const modifiedFormValues = {
            ...formValues,
            BusinessDetails: businessDetailsWithTrustDeed,
          } as FormValues;

          const applicationInput = transformUpdateApplicationInput({
            currentPage,
            formValues: modifiedFormValues,
          });
          submitStep(applicationInput, { ...additionalData, BusinessDetails: businessDetailsWithTrustDeed });
        } catch (error) {
          datadogRum.addAction('Failed to upload trust deed files', { error });
          resolve({ isStepSaved: false, message: 'Failed to upload trust deed documents' });
        }
      };

      switch (currentPage) {
        case APPLICATION_STEPS.FINANCE_PURCHASE:
          {
            datadogRum.addAction('Inside finance step');
            setApplicationData(newFormValues);
            trackStepSubmitted();
            resolve({ isStepSaved: true });
          }
          break;

        // If the user is a joint applicant, update the existing application else create a new application
        case APPLICATION_STEPS.ELIGIBILITY:
          {
            datadogRum.addAction('Inside Eligibility step');

            const isJoint = isJointApplicant();
            if (isJoint) {
              submitStep();
            } else {
              const applicationInput = transformCreateApplicationInput({
                formValues,
                user,
                quote,
              });

              if (applicationInput === null) {
                resolve({ isStepSaved: false });
                return;
              }
              createApplicationMutation(
                { input: applicationInput },
                {
                  onSuccess: (data: Record<string, unknown>) => {
                    datadogRum.addAction('Created application from quote');
                    setApplicationData({
                      ...newFormValues,
                      applicationId: (data?.createApplicationFromQuote as MutationResponse)?.applicationId,
                    });
                    trackStepSubmitted();
                    resolve({ isStepSaved: true });
                  },
                  onError: (errorResponse: unknown) => {
                    datadogRum.addAction('Failed create application from quote', {
                      name: 'Failed Create Application',
                      values: {
                        errorResponse,
                      },
                    });
                    handleUpdateFailure(errorResponse, resolve);
                  },
                },
              );
            }
          }
          break;

        // If the joint applicant details are included, add the joint applicant else update the existing application
        case APPLICATION_STEPS.HOUSEHOLD:
          {
            const householdDetails = formValues?.HouseholdDetails;

            if (householdDetails?.hasJointApplicant && householdDetails?.jointApplicant) {
              addJointApplicant(
                {
                  addJointApplicantInput: {
                    ...householdDetails.jointApplicant,
                    applicationId: applicationData.applicationId ?? '',
                    step: ApplicationStep.AddJointApplicant,
                  },
                },
                {
                  onSuccess: () => {
                    submitStep();
                  },
                  onError: (errorResponse: unknown) => handleUpdateFailure(errorResponse, resolve),
                },
              );
            } else {
              submitStep();
            }
          }
          break;

        case APPLICATION_STEPS.REPAYMENT_ACCOUNT: {
          const formPayment = formValues.PaymentDetails?.payment;

          const isSameAccountNumber = checkIfSameAccountNumber(formPayment, applicationData.PaymentDetails?.payment);

          const handleUpdatePayment = (methodId: string) => {
            const updatedPaymentDetails = {
              ...formValues,
              PaymentDetails: {
                ...formValues.PaymentDetails,
                payment: {
                  ...formValues.PaymentDetails?.payment,
                  methodId,
                },
              },
            } as FormValues;

            const applicationInput = transformUpdateApplicationInput({
              currentPage,
              formValues: updatedPaymentDetails,
            });

            if (applicationInput === null) {
              resolve({ isStepSaved: false });
              return;
            }
            submitStep(applicationInput, updatedPaymentDetails);
          };

          const handleFailedPayment = (errorResponse: unknown) => handleUpdateFailure(errorResponse, resolve);

          if (isSameAccountNumber && formPayment?.methodId) {
            handleUpdatePayment(formPayment.methodId);
          } else {
            onSavePaymentDetails(formValues.PaymentDetails, handleUpdatePayment, handleFailedPayment);
          }
          break;
        }
        case APPLICATION_STEPS.INCOME_REVIEW: {
          const incomeReviewFormValues = formValues.IncomeReview;
          const updatedIncome = {
            ...formValues,
            IncomeVerification: {
              ...formValues.IncomeVerification,
              applicant: {
                ...formValues.IncomeVerification?.applicant,
                usingOpenBanking: incomeReviewFormValues?.applicant.usingOpenBanking,
                income: incomeReviewFormValues?.applicant.income,
              },
            },
          } as FormValues;

          submitStep(undefined, updatedIncome);
          break;
        }

        case APPLICATION_STEPS.BUSINESS_DETAILS: {
          void handleTrustDeedUpload().catch((error: unknown) => {
            datadogRum.addAction('Unhandled error in trust deed upload', { error });
            resolve({ isStepSaved: false, message: 'Failed to process business details' });
          });
          break;
        }

        default:
          submitStep();
          break;
      }
    });

  return { saveStep };
};
